import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import React, { useCallback, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchInput } from '../../../component/form/SearchInput';
import { HorizontalBox } from '../../../component/layout/BoxLayout';
import { EmployeeClearFilterButton } from '../employeeStatusList/EmployeeClearFilterButton';
import { ContractDownloadButton } from './ContractDownloadButton';
import { ContractFilter, ContractFilterState, getSelectableStatuses } from './contractFilterState';
import { isBikeleasingContractStatus } from './model/contract-status';

interface Props {
  filterState: ContractFilterState;
  showStatusFilter?: boolean;
  hasContracts: boolean;
}

export const ContractFilterSelector: React.FC<Props> = ({ filterState, showStatusFilter = true, hasContracts }) => {
  const { t } = useTranslation('benefitsModule');
  const id = useId();

  useEffect(() => {}, [filterState.filter]);

  const handleFilterChange = useCallback(
    (key: keyof ContractFilter, value?: string) => {
      const setFilter = filterState.setFilter;
      setFilter((prev: ContractFilter) => ({
        ...prev,
        [key]: value,
      }));
    },
    [filterState.setFilter],
  );

  const handleChangeSearchText = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
    event => {
      handleFilterChange('searchText', event.target.value);
    },
    [handleFilterChange],
  );

  const handleChangeStatus = useCallback(
    (event: SelectChangeEvent) => {
      const value = event.target.value;
      const validStatus = isBikeleasingContractStatus(value) ? value : undefined;

      filterState.setFilter((filter: ContractFilter) => ({
        ...filter,
        selectedStatus: validStatus,
      }));
    },
    [filterState],
  );

  const handleRevertFilters = useCallback(() => {
    const setFilter = filterState.setFilter;
    setFilter((filter: ContractFilter) => {
      const clearedFilter = { ...filter };
      clearedFilter.selectedStatus = undefined;
      clearedFilter.searchText = undefined;
      return clearedFilter;
    });
  }, [filterState.setFilter]);

  const isFilterSet = !!filterState.filter.searchText || !!filterState.filter.selectedStatus;

  return (
    <HorizontalBox sx={{ marginBottom: 1.5 }}>
      <HorizontalBox sx={{ gap: 1, flexGrow: 1, alignItems: 'center' }}>
        <SearchInput
          data-test-id="search-bar"
          value={filterState.filter.searchText || ''}
          onChange={handleChangeSearchText}
          size="small"
          sx={{ width: 250 }}
        />
        {showStatusFilter && (
          <FormControl size="small">
            <InputLabel htmlFor={id}>{t('extensions:bikeleasing_v2.contractsTable.header.status')}</InputLabel>
            <Select
              data-test-id="contract-status-selector"
              label={t('extensions:bikeleasing_v2.contractsTable.header.status')}
              id={id}
              onChange={handleChangeStatus}
              value={filterState.filter.selectedStatus ?? ''}
              sx={{ width: 250, height: '100%', backgroundColor: 'background.paper' }}
            >
              {getSelectableStatuses(filterState.filter.contractPage)?.map(status => (
                <MenuItem key={status} value={status}>
                  {status && t(`extensions:bikeleasing_v2.contract.status.${status}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {isFilterSet && <EmployeeClearFilterButton onRevertFilters={handleRevertFilters} />}
      </HorizontalBox>
      <ContractDownloadButton hasContracts={hasContracts} />
    </HorizontalBox>
  );
};
